import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import { useMemo, useState, Suspense } from "react";
import { useSelector } from "react-redux";
import { CssBaseline, ThemeProvider, Box } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { themeSettings } from "./theme";
import { useAuth } from "providers";

import Sidebar from "scenes/global/NewSidebar";
import Navbar from "scenes/navbar";

import LoginPage from "scenes/loginPage";
import InventoryTab from "scenes/inventory/tabs/inventoryTab";
import InventorySetupTab from "scenes/inventory/tabs/inventorySetupTab";
import CustomerTab from "scenes/customer/tabs/customerTab";
import PurchaseTab from "scenes/purchase/tabs/purchaseTab";
import UserManagementTab from "scenes/userManagement/tabs/userManagementTab";
import AccountingTab from "scenes/accounting/tabs/accountingTab";
import HumanResourceTab from "scenes/humanResource/tabs/humanResourceTab";
import SettingsTab from "scenes/settings/tabs/settingsTab";
import BillingTab from "scenes/billing/tabs/billingTab";
import DashboardTab from "scenes/dashboards/tabs/dashboardTab";
import StockTransferTab from "scenes/stockTransfer/tabs/stockTransferTab";
import BusinessTab from "scenes/business/tabs/businessTab";
import UploadsTab from "scenes/upload/tabs/uploadTab";

import InvoiceForm from "scenes/billing/forms/invoiceForm";
import InvoiceDetailsView from "scenes/billing/views/invoiceDetailView";
import ProductLog from "scenes/inventory/tables/productLogs";
import AdjustmentForm from "scenes/inventory/forms/adjustmentForms/stockAdjustmentForm";
import PurchaseOrderForm from "scenes/purchase/forms/purchaseOrderForms/purchaseOrderForm";
import PurchaseViewForm from "scenes/purchase/forms/purchaseForms/purchaseViewForm";
import PurchaseOrderViewForm from "scenes/purchase/forms/purchaseOrderForms/purchaseOrderViewForm";
import EditPurchaseOrderForm from "scenes/purchase/forms/purchaseOrderForms/editPurchaseOrderFom";
import EmployeeDetailsForm from "scenes/humanResource/forms/employeeForms/employeeDetailsForm";
import UserGroupCreateForm from "scenes/userManagement/forms/userGroupCreateForm";
import EditUserGroupPermssionForm from "scenes/userManagement/forms/editGroupPermisions";
import ReportsForm from "scenes/reports/forms";
import PayslipForm from "scenes/humanResource/forms/payslipForms/payslipForm";
import EditPayslipForm from "scenes/humanResource/forms/payslipForms/editPayslipForm";
import PurchaseRequisitionForm from "scenes/purchase/forms/purchaseRequisitionForms/purchaseRequisitionForm";
import EditPurchaseRequisitionForm from "scenes/purchase/forms/purchaseRequisitionForms/editPurchaseRequisitionForm";
import RefundForm from "scenes/billing/forms/refundForm";
import SupplierReturnForm from "scenes/purchase/forms/returns/supplierReturnForm";
import PurchaseDraftForm from "scenes/purchase/forms/purchaseDraftForms/purchaseDraftForm";
import EditPurchaseDraftForm from "scenes/purchase/forms/purchaseDraftForms/editPurchaseDraftForm";
import PurchaseDraftViewForm from "scenes/purchase/forms/purchaseDraftForms/purchaseDraftViewForm";
import StockTransferCreateForm from "scenes/stockTransfer/forms/createStockTransferForm";
import EditStockTransferForm from "scenes/stockTransfer/forms/editStockTransferForm";
import ProductUploadProgressScreen from "scenes/upload/forms/productUploadProcessForm";
import StockTakeUploadProgressScreen from "scenes/upload/forms/stockTakeUploadProcessForm";
import InitialStockUploadProgressScreen from "scenes/upload/forms/initialStockUploadProcessForm";
import StockTakeReviewForm from "scenes/inventory/forms/stockTakeReviewForm";
import StockTakeManualForm from "scenes/inventory/forms/stockTakeManualForm";
import InvoiceEditForm from "scenes/billing/forms/invoiceEditForm";

import ItemPurchaseDetailReport from "scenes/reports/tables/purchase/itemPurchaseDetailReport";
import ItemPurchaseSummativeReport from "scenes/reports/tables/purchase/itemPurchaseSummativeReport";
import ItemSalesSummativeReport from "scenes/reports/tables/sales/itemSalesSummativeReport";
import ItemSalesReport from "scenes/reports/tables/sales/itemSalesReport";
import AgingReport from "scenes/reports/tables/inventory/agingReport";
import ItemTrends from "scenes/reports/tables/inventory/itemTrends";
import RecievableReport from "scenes/reports/tables/accounting/recievables";
import PayablesReport from "scenes/reports/tables/accounting/payables";
import SupplierPurchaseReport from "scenes/reports/tables/purchase/supplierPurchaseReport";
import BatchwiseItemStockReport from "scenes/reports/tables/inventory/batchwiseStockReport";
import CustomerReturnReport from "scenes/reports/tables/returns/customerReturnReport";
import SupplierReturnReport from "scenes/reports/tables/returns/supplierReturnReport";
import DeadStockReport from "scenes/reports/tables/inventory/deadStockReport";
import StockAdjustmentReport from "scenes/reports/tables/inventory/stockAdjustmentReport";
import BalanceSheetView from "scenes/accounting/views/balanceSheetView";
import ProfitAndLossView from "scenes/accounting/views/profitAndLossView";
import ChartNextAccounts from "scenes/accounting/tables/chartNextAccounts";
import LedgerTransactionsTable from "scenes/accounting/tables/ledgerTransactionsTable";
import { ErrorBoundary } from "components/ErrorBoundary";

// ─── Protected Route ────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { isAuthenticated: auth } = useAuth();
  const location = useLocation();

  if (!auth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// ─── App Shell (Sidebar + Navbar + Routes) ──────────────────────────────────
function AppShell({ isSidebar, setIsSidebar }) {
  return (
    <>
      <Navbar setIsSidebar={setIsSidebar} />
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Sidebar isSidebar={isSidebar} />
        <Box sx={{ flexGrow: 999 }}>
          <main className="content">
            <Box>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboards" />} />
                <Route path="/dashboards" element={<DashboardTab />} />
                <Route path="/customer" element={<CustomerTab />} />
                <Route path="/billing" element={<BillingTab />} />
                <Route path="/invoiceForm" element={<InvoiceForm />} />
                <Route path="/invoiceEditForm" element={<InvoiceEditForm />} />
                <Route path="/invoiceDetails" element={<InvoiceDetailsView />} />
                <Route path="/purchase" element={<PurchaseTab />} />
                <Route path="/inventory" element={<InventoryTab />} />
                <Route path="/setup" element={<InventorySetupTab />} />
                <Route path="/settings" element={<SettingsTab />} />
                <Route path="/userManagement" element={<UserManagementTab />} />
                <Route path="/accounting" element={<AccountingTab />} />
                <Route path="/chartNextAccounts" element={<ChartNextAccounts />} />
                <Route path="/humanResource" element={<HumanResourceTab />} />
                <Route path="/transfers" element={<StockTransferTab />} />
                <Route path="/fileUploads" element={<UploadsTab />} />
                <Route path="/business" element={<BusinessTab />} />
                <Route path="/reports" element={<ReportsForm />} />
                <Route path="/purchaseViewForm" element={<PurchaseViewForm />} />
                <Route path="/purchaseDraftForm" element={<PurchaseDraftForm />} />
                <Route path="/purchaseDraftViewForm" element={<PurchaseDraftViewForm />} />
                <Route path="/editPurchaseDraftForm" element={<EditPurchaseDraftForm />} />
                <Route path="/productLogList" element={<ProductLog />} />
                <Route path="/adjustmentForm" element={<AdjustmentForm />} />
                <Route path="/purchaseOrderForm" element={<PurchaseOrderForm />} />
                <Route path="/editPurchaseOrderForm" element={<EditPurchaseOrderForm />} />
                <Route path="/purchaseOrderViewForm" element={<PurchaseOrderViewForm />} />
                <Route path="/payslipForm" element={<PayslipForm />} />
                <Route path="/productUploadProgress" element={<ProductUploadProgressScreen />} />
                <Route path="/stockTakeUploadProgress" element={<StockTakeUploadProgressScreen />} />
                <Route path="/initialStockUploadProgress" element={<InitialStockUploadProgressScreen />} />
                <Route path="/stockTakeReview" element={<StockTakeReviewForm />} />
                <Route path="/stockTakeManualForm" element={<StockTakeManualForm />} />
                <Route path="/editPayslipForm" element={<EditPayslipForm />} />
                <Route path="/purchaseRequisitionForm" element={<PurchaseRequisitionForm />} />
                <Route path="/editPurchaseRequisitionForm" element={<EditPurchaseRequisitionForm />} />
                <Route path="/refundForm" element={<RefundForm />} />
                <Route path="/supplierReturnForm" element={<SupplierReturnForm />} />
                <Route path="/stockTransferForm" element={<StockTransferCreateForm />} />
                <Route path="/editStockTransferForm" element={<EditStockTransferForm />} />
                <Route path="/employeeDetails" element={<EmployeeDetailsForm />} />
                <Route path="/userGroup" element={<UserGroupCreateForm />} />
                <Route path="/userGroupPermissions" element={<EditUserGroupPermssionForm />} />
                <Route path="/itemPurchaseDetailReport" element={<ItemPurchaseDetailReport />} />
                <Route path="/itemPurchaseSummativeReport" element={<ItemPurchaseSummativeReport />} />
                <Route path="/balanceSheet" element={<BalanceSheetView />} />
                <Route path="/payables" element={<PayablesReport />} />
                <Route path="/profit&Loss" element={<ProfitAndLossView />} />
                <Route path="/recievables" element={<RecievableReport />} />
                <Route path="/agingReport" element={<AgingReport />} />
                <Route path="/itemTrends" element={<ItemTrends />} />
                <Route path="/supplierPurchase" element={<SupplierPurchaseReport />} />
                <Route path="/itemSalesReport" element={<ItemSalesReport />} />
                <Route path="/itemSalesSummativeReport" element={<ItemSalesSummativeReport />} />
                <Route path="/batchwiseItemStockReport" element={<BatchwiseItemStockReport />} />
                <Route path="/deadStockReport" element={<DeadStockReport />} />
                <Route path="/customerReturnReport" element={<CustomerReturnReport />} />
                <Route path="/supplierReturnReport" element={<SupplierReturnReport />} />
                <Route path="/stockAdjustmentReport" element={<StockAdjustmentReport />} />
                <Route path="/ledgerTransactions" element={<LedgerTransactionsTable />} />
                <Route path="*" element={<Navigate to="/dashboards" />} />
              </Routes>
            </Box>
          </main>
        </Box>
      </Box>
    </>
  );
}

// ─── Root App ────────────────────────────────────────────────────────────────
function App() {
  const mode = useSelector((state) => state.authReducer.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  const [isSidebar, setIsSidebar] = useState(true);

  return (
    <div className="app">
      <ErrorBoundary>
        <Suspense>
          <BrowserRouter>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/" element={<LoginPage />} />

                {/* All protected routes */}
                <Route
                  path="*"
                  element={
                    <ProtectedRoute>
                      <AppShell isSidebar={isSidebar} setIsSidebar={setIsSidebar} />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </ThemeProvider>
          </BrowserRouter>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

export default App;